import { Link } from "react-router-dom";
import { useSEO } from '@/hooks/useSEO';
import { Button } from "@/components/ui/button";
import { MessageSquare, CheckCircle, ArrowRight, Zap } from "lucide-react";

export default function BlogCompressFilesForSlack() {
  useSEO({
    title: 'Compress Files for Slack — Stay Under the 10MB Free Plan Limit 2026 | SlimFile Blog',
    description: 'Compress PDFs, images, and documents to share on Slack without hitting the 10MB file size limit on free plans. Free compression tools that work instantly.',
    canonical: 'https://slim-file.com/blog/compress-files-for-slack',
  });
  return (
    <div className="min-h-screen pt-28 bg-white">
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 mx-auto mb-6"><MessageSquare className="w-10 h-10 text-purple-600" /></div>
          <span className="text-sm font-semibold text-red-600 uppercase tracking-wider">File Sharing</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-6 leading-tight">Compress Files for Slack</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">Slack's free plan caps file uploads at 10MB. Compress your PDFs and images before sharing to stay under the limit — instantly, for free.</p>
          <Link to="/compress"><Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl font-semibold">Compress Files Free <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          <p className="text-sm text-gray-500 mt-4">Updated January 2026 · 4 min read</p>
        </div>
      </section>

      <article className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-3xl space-y-10">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Slack File Size Limits by Plan</h2>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left p-3 font-semibold text-gray-700">Slack Plan</th>
                  <th className="text-left p-3 font-semibold text-gray-700">File Upload Limit</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Storage</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="bg-yellow-50"><td className="p-3 font-semibold">Free</td><td className="p-3 font-semibold text-red-600">10MB per file</td><td className="p-3">5GB total</td></tr>
                  <tr><td className="p-3">Pro ($7.25/mo)</td><td className="p-3">1GB per file</td><td className="p-3">10GB per member</td></tr>
                  <tr><td className="p-3">Business+ ($12.50/mo)</td><td className="p-3">1GB per file</td><td className="p-3">Unlimited</td></tr>
                  <tr><td className="p-3">Enterprise Grid</td><td className="p-3">1GB per file</td><td className="p-3">Unlimited</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-600 text-sm mt-3">If you're on the free plan, a 10MB limit means most PDF reports, presentations, and high-res screenshots need to be compressed before sharing.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Compress PDFs for Slack — Step by Step</h2>
            <div className="space-y-4">
              {[
                { step: "1", title: "Open slim-file.com/compress-pdf-online", desc: "Works in Chrome, Firefox, Edge, Safari — no account needed." },
                { step: "2", title: "Upload your PDF", desc: "Drag the file or click to browse. Files are processed in your browser session." },
                { step: "3", title: "Download the compressed PDF", desc: "Typical results: 20MB → 1–2MB. Well under Slack's 10MB limit." },
                { step: "4", title: "Share in Slack", desc: "Click the attachment (📎) icon in Slack's message bar and upload the compressed file." },
              ].map(item => (
                <div key={item.step} className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm shrink-0">{item.step}</div>
                  <div><p className="font-semibold text-gray-900">{item.title}</p><p className="text-gray-600 text-sm mt-1">{item.desc}</p></div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Alternative: Share a Link Instead of File</h2>
            <p className="text-gray-600 mb-4">For very large files that can't be compressed under 10MB, share a Google Drive or Dropbox link in Slack instead:</p>
            <ol className="list-decimal list-inside space-y-3 text-gray-600">
              <li>Upload the large file to Google Drive</li>
              <li>Right-click → Share → Copy Link</li>
              <li>Paste the link in Slack — Slack will generate a preview automatically for most file types</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Slack File Sharing Tips</h2>
            <ul className="space-y-3">
              {[
                "Compress screenshots before sharing — PNG screenshots of design work can easily be 5–10MB",
                "For recurring documents (weekly reports), compress once and reuse",
                "Use Slack's built-in Google Drive integration to share large files without compression",
                "GIFs shared in Slack count toward the 10MB limit — compress GIFs with SlimFile first",
                "Images shared from URL (not uploaded) don't count toward the file size limit",
              ].map((tip, i) => (
                <li key={i} className="flex gap-3 text-gray-600"><CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" /><span>{tip}</span></li>
              ))}
            </ul>
          </section>
        </div>
      </article>

      <section className="py-16 px-4 bg-gradient-to-r from-red-600 to-orange-500">
        <div className="container mx-auto max-w-3xl text-center text-white">
          <Zap className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">Beat Slack's 10MB Limit — Compress Free</h2>
          <p className="text-red-100 mb-8 text-lg">Instant compression. No account. Works on any device.</p>
          <Link to="/compress"><Button className="bg-white text-red-600 hover:bg-red-50 px-10 py-4 rounded-xl font-bold text-lg">Compress Files Free <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
